import { Box } from '@mui/material';
import { colors, Tag, Typography } from '@sweep-io/sweep-design';
import { CloudCross } from '@sweep-io/sweep-design/dist/icons';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactTimeAgo from 'react-time-ago';
import { useCrmOrgs } from '../useCrmOrgs';
import { selectUserById } from '../../../../reducers/accountUsersReducer';
import { OrgDot } from '../../../common/OrgDot';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { DeleteEnvCard } from './DeleteEnvCard';
import { FactoryResetCard } from './FactoryResetCard';
import { EditableLabelRef } from '../../../common/EditableLabel';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { isOrgConnectionError } from '../../../../hooks/global-reducer/environmentsHelper';
import { selectConnectedCrmOrgs } from '../environmentsReducer';
import {
  selectDefaultCreationCrmOrgId,
  selectUserMcpOrgId,
} from '../../../../reducers/userInfoReducer';
import { ACTIONS_EVENTS } from '../../../../services/events';
import { useChangeEnvironment } from '../../../../hooks/useChangeEnvironment';
import { CrmOrg } from '../../../../types/CrmOrgs';
import { CompleteSfSetupDialog } from '../../../common/complete-sf-setup/CompleteSfSetupDialog';
import { EnvironmentActionButtons } from './EnvironmentActionButtons';
import { RefreshOrReconnect } from './RefreshOrReconnect';
import { EnvironmentName } from './EnvironmentName';

export const EnvironmentCard = ({ env, allowDelete }: { env: CrmOrg; allowDelete: boolean }) => {
  const [completeSetupDialogOpen, setCompleteSetupDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const { orgFactoryReset, setAsMcp } = useFeatureToggle();
  const { addNotification } = useSweepNotifications();
  const envs = useSelector(selectConnectedCrmOrgs);
  const defaultCreationCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { changeEnvironment } = useChangeEnvironment();
  const userMcpOrgId = useSelector(selectUserMcpOrgId);

  const editableLabelRef = useRef<EditableLabelRef>(null);

  const { fetchOrg, removeCrmOrg } = useCrmOrgs();

  const creatorUserInfo = useSelector(selectUserById(env.createdById));

  const hasError = isOrgConnectionError(env.fetchStatus);

  const closeMenu = useCallback(() => {
    setIsDeleting(false);
    setIsResetting(false);
    setCompleteSetupDialogOpen(false);
  }, []);

  const confirmDeleteHandler = useCallback(async () => {
    const envType = env.isMain ? 'Production' : 'Sandbox';
    const biEvent = {
      name: ACTIONS_EVENTS.removeEnv,
      props: { type: envType },
    };
    try {
      let _env: CrmOrg | undefined;
      if (defaultCreationCrmOrgId === env.id) {
        _env = envs.find((e) => e.id !== env.id);
        if (_env) {
          changeEnvironment(_env.id);
          await removeCrmOrg(env.id, biEvent);
          closeMenu();
          return;
        }
      }
      await removeCrmOrg(env.id, biEvent);
    } catch (e) {
      addNotification({
        message: 'Unknown error',
        variant: SweepNotificationVariant.Error,
      });
    }
  }, [
    addNotification,
    defaultCreationCrmOrgId,
    env.id,
    env.isMain,
    envs,
    changeEnvironment,
    removeCrmOrg,
    closeMenu,
  ]);

  const confirmFactoryResetHandler = async () => {
    await fetchOrg(env, true);
    closeMenu();
  };

  if (isDeleting) {
    return <DeleteEnvCard onCancel={closeMenu} onConfirm={confirmDeleteHandler} />;
  }

  if (isResetting && orgFactoryReset) {
    return <FactoryResetCard onCancel={closeMenu} onConfirm={confirmFactoryResetHandler} />;
  }

  return (
    <Box
      py={2}
      px={2}
      mx={-2}
      sx={{
        '.sweep__env-action': {
          position: 'absolute',
          right: 0,
          display: 'none',
        },
        '&:hover': {
          backgroundColor: colors.grey[100],
          '.sweep__env-action': {
            display: 'block',
          },
        },
      }}
      onDoubleClick={() => {
        navigator.clipboard.writeText(env.id);
        console.log('copied CRMORG id to clipboard', env.id);
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <EnvironmentName env={env} editableLabelRef={editableLabelRef} />
          {env.isMain && <OrgDot styles={{ top: '1px', marginRight: '8px' }} />}
          {userMcpOrgId === env.id && setAsMcp && <Tag label="MCP" color={colors.grey[150]} />}
        </Box>
        <Box gap={0.5} display="flex" alignItems="center">
          {hasError && <CloudCross />}

          <EnvironmentActionButtons
            editableLabelRef={editableLabelRef}
            env={env}
            allowDelete={allowDelete}
            setDeleteDialogOpenEnv={() => setIsDeleting(true)}
            setCompleteSetupDialogOpenEnv={() => setCompleteSetupDialogOpen(true)}
            setFactoryResetDialogOpenEnv={() => setIsResetting(true)}
          />
        </Box>
      </Box>
      <Box my={0.5}>
        <Typography variant="body">Created by {creatorUserInfo?.name}</Typography>
      </Box>
      <Box display="flex" position="relative" justifyContent="space-between" alignItems="center">
        <Typography variant="body">
          Last refreshed <ReactTimeAgo date={new Date(env.updatedAt)} locale="en-US" />
        </Typography>
        <RefreshOrReconnect env={env} />
      </Box>

      {completeSetupDialogOpen && (
        <CompleteSfSetupDialog
          cancelDialog={() => setCompleteSetupDialogOpen(false)}
          onCompleteItemsCallback={() => setCompleteSetupDialogOpen(false)}
          title="Complete Your Setup"
          subtitle="Get started with Sweep by following these quick steps"
          crmOrgId={env.id}
        />
      )}
    </Box>
  );
};
